import { mapActions, mapGetters } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import _ from "lodash";
import dynamicsHead from "@/mixins/dynamics-head";
import product from "@/components/product/index.vue";
import groupProduct from "@/components/group-product/index.vue";
import categoryFilter from "@/modules/catalog/components/category-filter/index.vue";
import sortSelect from "@/modules/catalog/components/sort-select/index.vue";
import catalogHero from "./components/catalog-hero/index.vue";
import categoryCards from "./components/category-cards/index.vue";
import brandArticle from "./components/brand-article/index.vue";

const STRUCTURAL_FILTER_KEYS = ["brands", "bands", "series", "collections", "collection"];

// Структурні секції за slug, які не мають рендеритись як атрибутний фільтр у сайдбарі.
// "aksesuar"/"aksesuari" — аксесуари це бренд (/brand/:slug), а не атрибутний фільтр;
// якщо /catalog/filters віддає секцію "Аксесуари" (categories id 72) — ховаємо її.
// Slug КАТЕГОРІЇ різниться між середовищами: стейдж — "aksesuar", прод — "aksesuari"
// (підтверджено бекендом 2026-08-06), тому тримаємо обидва. Не плутати зі slug БРЕНДУ
// "aksesuari" — це інша сутність (/brand/aksesuari).
// accessories/samplers = бренди (/brand/:slug); outlet = категорія (/outlet, pageType:"outlet").
const STRUCTURAL_FILTER_SLUGS = ["aksesuar", "aksesuari"];

const VALID_PAGE_TYPES = ["landing", "brand", "collection", "outlet"];

export default {
  name: "catalog-hub",
  mixins: [dynamicsHead],
  components: {
    product,
    groupProduct,
    categoryFilter,
    sortSelect,
    catalogHero,
    categoryCards,
    brandArticle,
  },
  props: {
    pageType: {
      type: String,
      required: true,
      validator: v => VALID_PAGE_TYPES.includes(v),
    },
    slug: {
      type: String,
      default: "",
    },
    collection: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      hero: null,
      cards: [],
      filters: [],
      products: [],
      loadingMeta: false,
      loadingFilters: false,
      loadingProducts: false,
      mobileFilterOpen: false,
      paginator: {
        hasNextPage: false,
        page: 1,
        perPage: 12,
      },
    };
  },
  computed: {
    ...mapGetters({
      isAuthenticated: "auth/isAuthenticated",
      favourites: "favorites/whichList",
      currentPromotion: "promotions/currentPromotion",
      isProfessional: "profile/isProfessional",
      isDistributor: "profile/isDistributor",
      page: "pages/page",
    }),
    contextSlug() {
      return this.slug || this.$route.params.slug || "";
    },
    collectionSlug() {
      if (this.pageType === "collection") return this.contextSlug;
      return "";
    },
    landingMode() {
      if (this.pageType !== "landing") return null;
      const q = this.$route.query;
      if (q.sale_banners) return "promotion";
      return "default";
    },
    showHero() {
      if (this.pageType === "landing" && this.landingMode !== "default") return false;
      return !!(this.heroData && (this.heroData.metaTitle || this.heroData.title));
    },
    showCards() {
      // Картки лишилися тільки на лендингу (бренди + колекції).
      // Дрілдаун-картки серій прибрано — серії тепер фільтр у сайдбарі.
      return this.pageType === "landing" && this.landingMode === "default";
    },
    showPromotionBanner() {
      return this.landingMode === "promotion";
    },
    // Розгорнутий опис бренду/колекції: description_blocks приходять тільки зі
    // списку /catalog/categories (у відповіді /products цього ключа немає взагалі).
    // Структура однакова для брендів і колекцій; де опис ще не завели — [].
    descriptionBlocks() {
      return (this.hero && this.hero.descriptionBlocks) || [];
    },
    showBrandArticle() {
      return this.descriptionBlocks.length > 0;
    },
    heroData() {
      return this.hero;
    },
    heroVariant() {
      // Outlet — full-bleed банер у стилях старого /catalog?categories=outlet.
      // outlet — окрема сторінка-категорія (/outlet), а не Collection.
      if (this.pageType === "outlet") return "outlet";
      return "";
    },
    // Розкладка hero. "split" — H1 над каталогом, опис+банер під ним (бренди й серії:
    // серія = ?series= на сторінці бренду, hero лишається брендовим). "full" — все
    // разом зверху (колекції напр. /collection/mens-health, outlet) — як раніше.
    heroLayout() {
      return this.pageType === "brand" ? "split" : "full";
    },
    cardsType() {
      return "category";
    },
    lockedParams() {
      switch (this.pageType) {
        case "brand":
          return { brands: this.contextSlug };
        case "collection":
          return { collection: this.contextSlug };
        case "outlet":
          // outlet — Category на беку. Тільки з ?categories=outlet вмикається
          // аутлетне сортування цін у ProductService::editProductPrices().
          return { categories: "outlet" };
        default:
          return {};
      }
    },
    visibleFilters() {
      return this.filters.filter(
        item => !STRUCTURAL_FILTER_KEYS.includes(item.key) && !STRUCTURAL_FILTER_SLUGS.includes(item.slug)
      );
    },
    // Вузол поточного контексту (бренд/колекція) у дереві /catalog/filters.
    // Ключі структурного вузла: бренд → "bands"/"brands", колекція → "collections"/"collection".
    contextNode() {
      if (this.pageType !== "brand" && this.pageType !== "collection") return null;
      const contextKeys = this.pageType === "brand" ? ["bands", "brands"] : ["collections", "collection"];
      const findContext = nodes => {
        for (const node of nodes || []) {
          if (!node) continue;
          if (contextKeys.includes(node.key) && node.slug === this.contextSlug) return node;
          const found = node.child && node.child.data && findContext(node.child.data);
          if (found) return found;
        }
        return null;
      };
      return findContext(this.filters);
    },
    // Синтетична група-фільтр «Серії» з чекбоксами (мультивибір → ?series=a,b).
    // Показуємо на сторінці бренду АБО колекції — серії саме цього контексту.
    // Бекенд віддає їх у /catalog/filters вкладеними під вузол бренду/колекції
    // як key:"series" (для колекції правка така сама, як для бренду). На лендингу серій немає.
    seriesFilterGroup() {
      if (!this.contextNode) return null;
      // Рекурсивно збираємо серії будь-якої глибини під вузлом контексту:
      // бренд → серії (1 рівень) або колекція → [бренди →] серії (кросбренд). Дедуп за id.
      const collectSeries = (nodes, seen = new Set()) => {
        const out = [];
        (nodes || []).forEach(node => {
          if (!node) return;
          if (node.key === "series" && node.slug) {
            if (seen.has(node.id)) return;
            seen.add(node.id);
            out.push({ id: node.id, key: "series", slug: node.slug, title: node.title, isOpened: false });
          } else if (node.child && node.child.data) {
            out.push(...collectSeries(node.child.data, seen));
          }
        });
        return out;
      };
      const series = collectSeries(this.contextNode.child && this.contextNode.child.data);
      if (!series.length) return null;
      return {
        id: "series-group",
        key: "series",
        slug: "series-group",
        title: this.$t("series"),
        isOpened: true,
        child: { data: series },
      };
    },
    // Синтетична група-фільтр «Бренди» — коли під вузлом бренду лежать не серії,
    // а інші бренди (кейс «Пробники»: всі пробники в бренді probniki, але кожен
    // знає «свій» бренд — renew/alphascience/genidentity). Діти мають key:"brands",
    // тобто їхній slug іде в той самий параметр `brands`, що й батьківський:
    // ?brands=probniki,renew = «бренд Пробники І бренд для фільтрування Renew».
    // Мердж батька з дитиною робить loadCatalogProducts (див. mergeCsv).
    //
    // Підтримуємо дві структури відповіді, щоб не залежати від порядку викочування:
    // 1) плоский список брендів одразу в child.data (як зараз на стейджі) — заголовок
    //    беремо зі свого ключа $t("brands");
    // 2) обгортка { key:"brands", child:{ data:[…бренди] } } з власним title (бекенд
    //    домовився віддати так, щоб ru-заголовок приїжджав з бекенду) — тоді беремо
    //    дітей і title з обгортки.
    brandFilterGroup() {
      if (this.pageType !== "brand" || !this.contextNode) return null;
      const children = (this.contextNode.child && this.contextNode.child.data) || [];
      const brandNodes = children.filter(node => node && node.key === "brands");
      // Обгортка — вузол brands з власними дітьми (у листа-бренда їх немає).
      const wrapper = brandNodes.find(node => node.child && node.child.data && node.child.data.length);
      const source = wrapper ? wrapper.child.data : brandNodes;
      const brands = (source || [])
        .filter(node => node && node.slug)
        .map(node => ({ id: node.id, key: "brands", slug: node.slug, title: node.title, isOpened: false }));
      if (!brands.length) return null;
      return {
        id: "brands-group",
        key: "brands",
        slug: "brands-group",
        title: (wrapper && wrapper.title) || this.$t("brands"),
        isOpened: true,
        child: { data: brands },
      };
    },
    // Серії/бренди зверху, далі — категорійні/атрибутні фільтри як раніше.
    sidebarFilters() {
      const groups = [this.brandFilterGroup, this.seriesFilterGroup].filter(Boolean);
      return [...groups, ...this.visibleFilters];
    },
    hasActiveFilters() {
      // lockedParams виводяться з роуту (path), а не з query, тож будь-який ключ
      // у query — це реальний фільтр користувача (показуємо «скинути»). Зокрема:
      // ?categories=… на /outlet (outlet шлеться locked-параметром, в URL його немає)
      // і ?brands=… на /brand/probniki (дочірній бренд поверх locked-батька).
      // Ключі, які resetFilter не скидає, фільтром не вважаємо.
      const ignored = ["page", "sort_filter_type", "catalog_type"];
      return Object.keys(this.$route.query).some(key => !ignored.includes(key));
    },
    catalogType() {
      return this.$route.query.catalog_type === "groups" ? "groups" : "table";
    },
    productsList() {
      if (!this.isAuthenticated || !this.favourites) {
        return this.products;
      }
      const favourites = _.map(this.favourites, "productId");
      const markAsSelected = item => {
        item.isFavorite = favourites.includes(item.id);
        return item;
      };
      if (this.catalogType === "table") {
        return _.map(this.products, markAsSelected);
      }
      return this.products.map(group => {
        _.map(group.products, markAsSelected);
        return group;
      });
    },
    pageTitle() {
      return this.$t("products");
    },
    breadcrumbsName() {
      return this.pageType === "landing" ? "products" : this.pageType;
    },
    breadcrumbsParams() {
      return {
        hero: this.hero,
        contextSlug: this.contextSlug,
        collectionSlug: this.collectionSlug,
      };
    },
    canonicalUrl() {
      const base = process.env.VUE_APP_URL || "";
      return base.replace(/\/$/, "") + this.$route.path;
    },
    analyticsListId() {
      switch (this.pageType) {
        case "landing":
          return "Catalog";
        case "brand": {
          const title = this.hero && (this.hero.title || this.hero.metaTitle);
          return `Brand: ${title || this.contextSlug}`;
        }
        case "collection": {
          const title = this.hero && (this.hero.title || this.hero.metaTitle);
          return `Collection: ${title || this.contextSlug}`;
        }
        case "outlet":
          return "Outlet";
        default:
          return "Catalog";
      }
    },
  },
  watch: {
    $route() {
      this.resetAndReload();
    },
  },
  async created() {
    await Promise.all([this.loadMeta(), this.loadFilters(), this.loadProducts(), this.loadLandingExtras()]);
    this.applyMetaInfo();
  },
  methods: {
    ...mapActions({
      fetchCategories: "catalog/GET_CATEGORIES",
      fetchCatalogFilters: "products/GET_CATALOG_FILTERS",
      fetchProductsForCatalog: "products/GET_PRODUCTS_FOR_CATALOG",
      fetchPromotionInfo: "promotions/FETCH_PROMOTION",
      fetchPage: "pages/GET_PAGE",
    }),
    async loadMeta() {
      this.loadingMeta = true;
      try {
        // Outlet — це Page (config_type=outlet), його немає в GET_CATEGORIES,
        // тож hero/мету беремо з /pages/outlet.
        if (this.pageType === "outlet") {
          await this.fetchPage("outlet");
          const page = this.page;
          if (page) {
            const banner = page.banners && page.banners.data && page.banners.data[0];
            this.hero = { ...page, type: "outlet", bannerImage: (banner && banner.image) || page.image || "" };
          } else {
            this.hero = null;
          }
          return;
        }
        // categories/{slug}/series прибрано на бекенді: hero бренду/колекції
        // беремо зі списку /catalog/categories (там є meta для кожного запису).
        const response = await this.fetchCategories();
        const categories = response.categories || [];
        if (this.pageType === "landing") {
          this.cards = categories;
        } else if (this.pageType === "brand" || this.pageType === "collection") {
          const match = categories.find(item => item.type === this.pageType && item.slug === this.contextSlug);
          if (!match) {
            this.$router.replace({ name: "page-not-found" });
            return;
          }
          this.hero = { ...match, type: this.pageType };
        }
      } catch (e) {
        if (!(e && e.status === 404)) {
          console.error("catalog-hub loadMeta:", e);
        }
      } finally {
        this.loadingMeta = false;
      }
    },
    async loadLandingExtras() {
      if (this.pageType !== "landing") return;
      try {
        await this.fetchPage("catalog");
        if (this.landingMode === "promotion" && this.$route.query.sale_banners) {
          await this.fetchPromotionInfo(this.$route.query.sale_banners);
        }
      } catch (e) {
        console.error("catalog-hub loadLandingExtras:", e);
      }
    },
    async loadFilters() {
      this.loadingFilters = true;
      try {
        this.filters = await this.fetchCatalogFilters();
      } catch (e) {
        console.error("catalog-hub loadFilters:", e);
      } finally {
        this.loadingFilters = false;
      }
    },
    async loadProducts() {
      this.loadingProducts = true;
      if (this.paginator.page === 1) {
        this.products = [];
      }
      try {
        await this.loadCatalogProducts();
      } catch (e) {
        if (!(e && e.status === 404)) {
          console.error("catalog-hub loadProducts:", e);
        }
      } finally {
        this.loadingProducts = false;
        this.pushAnalytics();
      }
    },
    async loadCatalogProducts() {
      const urlQuery = cloneDeep(this.$route.query);
      const payload = {
        catalog_type: urlQuery.catalog_type,
        sort_filter_type: urlQuery.sort_filter_type || "bestsellers",
        // Бренд сторінки — locked-параметр, але під ним можуть бути дочірні бренди
        // («Пробники» → renew/alphascience/…), які пишуться в ?brands=. Бекенд чекає
        // пару «батько,дитина» (?brands=probniki,renew), тому мерджимо, а не замінюємо.
        brands: this.mergeCsv(this.lockedParams.brands, urlQuery.brands),
        series: this.lockedParams.series || urlQuery.series,
        collection: this.lockedParams.collection || urlQuery.collection,
        categories: this.mergeCsv(this.lockedParams.categories, urlQuery.categories),
        sale_banners: urlQuery.sale_banners,
        page: this.paginator.page,
        perPage: this.paginator.perPage,
      };
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined || payload[key] === "") {
          delete payload[key];
        }
      });
      const response = await this.fetchProductsForCatalog(payload);
      const data = (response && response.data) || [];
      this.products.push(...data);
      this.paginator.hasNextPage = !!(response && response.links && response.links.next);
    },
    // Locked-контекст роуту + значення з query в одному csv-параметрі.
    // Locked завжди перший: бекенд читає ?brands=probniki,renew як «бренд Пробники
    // І бренд для фільтрування Renew», а ?categories=outlet,… — як аутлет + атрибути.
    mergeCsv(locked, queryValue) {
      if (!locked) return queryValue;
      const parts = queryValue
        ? queryValue
            .toString()
            .split(",")
            .map(s => s.trim())
            .filter(Boolean)
        : [];
      if (!parts.includes(locked)) parts.unshift(locked);
      return parts.join(",");
    },
    async paginateProducts() {
      this.paginator.page += 1;
      await this.loadProducts();
    },
    pushAnalytics() {
      if (typeof window === "undefined" || !window.dataLayer) return;
      if (this.catalogType !== "table") return;
      if (!this.productsList || !this.productsList.length) return;
      const userType = (() => {
        if (!this.isAuthenticated) return "unauthorized";
        if (this.isProfessional) return "professional";
        if (this.isDistributor) return "distributor";
        return "regular";
      })();
      const listId = this.analyticsListId;
      const items = this.productsList.map(item => {
        const firstPrice = item.productPrices && item.productPrices.data && item.productPrices.data[0];
        return {
          item_id: firstPrice ? firstPrice.article : item.id,
          item_name: item.title,
          affiliation: "SkinOn-Cosmetics",
          coupon: "",
          discount: 0.0,
          index: 0,
          item_brand: (item.brand && item.brand.title) || "SkinOn",
          item_category: (item.brandSeries && item.brandSeries.title) || "",
          item_category2: userType,
          item_list_id: listId,
          item_list_name: listId,
          location_id: "ChIJIQBpAG2ahYAR_6128GcTUEo",
          price: firstPrice ? firstPrice.price : 0,
          quantity: 1,
        };
      });
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "view_item_list",
        ecommerce: {
          item_list_id: listId,
          item_list_name: listId,
          items,
        },
      });
    },
    async resetAndReload() {
      this.paginator.page = 1;
      this.paginator.hasNextPage = false;
      this.products = [];
      this.hero = null;
      this.cards = [];
      await Promise.all([this.loadMeta(), this.loadProducts(), this.loadLandingExtras()]);
      this.applyMetaInfo();
    },
    resetFilter() {
      const cleanQuery = {};
      if (this.$route.query.catalog_type) {
        cleanQuery.catalog_type = this.$route.query.catalog_type;
      }
      if (this.$route.query.sort_filter_type) {
        cleanQuery.sort_filter_type = this.$route.query.sort_filter_type;
      }
      this.$router
        .push({
          name: this.$route.name,
          params: this.$route.params,
          query: cleanQuery,
        })
        .catch(() => {});
    },
    toggleMobileFilter() {
      this.mobileFilterOpen = !this.mobileFilterOpen;
      const html = document.getElementById("html");
      if (html) {
        if (this.mobileFilterOpen) {
          html.classList.add("hide");
        } else {
          html.classList.remove("hide");
        }
      }
    },
    applyMetaInfo() {
      this.removeNoIndexNoFollow();
      if (this.pageType === "landing" && this.landingMode === "default" && this.page) {
        this.setPageMetaInfo(this.page);
        if (this.$route.query.brands && this.$route.query.brands.split(",").length > 1) {
          this.addNoIndexNoFollow();
        }
      } else {
        const meta = this.buildPageMeta();
        if (meta) {
          this.setPageMetaInfo(meta);
          if (meta.ogImage) {
            this.changeMetaInfo("og:image", meta.ogImage);
          }
        }
      }
      this.setCanonical(this.canonicalUrl);
      this.applyBreadcrumbsJsonLd();
      this.applyFaqJsonLd();
    },
    // FAQPage з блоків type:"dropdown" — питання/відповіді вже приходять
    // структуровано, тож розмітка будується з тих самих даних, що й видимий акордеон.
    // Google дозволяє в acceptedAnswer обмежений HTML (p, ul/ol/li, a, b/strong…),
    // тому content віддаємо як є.
    buildFaqJsonLd() {
      const entities = [];
      this.descriptionBlocks.forEach(block => {
        if (!block || block.type !== "dropdown") return;
        (block.items || []).forEach(item => {
          const question = item && item.title && item.title.trim();
          const answer = item && item.content && item.content.trim();
          if (!question || !answer) return;
          entities.push({
            "@type": "Question",
            name: question,
            acceptedAnswer: {
              "@type": "Answer",
              text: answer,
            },
          });
        });
      });
      if (!entities.length) return null;
      return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: entities,
      };
    },
    applyFaqJsonLd() {
      const ld = this.buildFaqJsonLd();
      if (!ld) {
        this.removeFaqJsonLd();
        return;
      }
      this.setFaqJsonLd(ld);
    },
    buildBreadcrumbChain() {
      const chain = [
        { title: this.$t("home"), route: { name: "main" } },
        { title: this.$t("catalog"), route: { name: "products" } },
      ];
      if (this.pageType === "brand" || this.pageType === "collection" || this.pageType === "outlet") {
        const title = this.hero && (this.hero.title || this.hero.metaTitle);
        if (title) chain.push({ title });
      }
      return chain;
    },
    applyBreadcrumbsJsonLd() {
      const chain = this.buildBreadcrumbChain();
      if (chain.length < 2) {
        this.removeBreadcrumbsJsonLd();
        return;
      }
      const base = (process.env.VUE_APP_URL || "").replace(/\/$/, "");
      const lang = this.$route.params.lang;
      const ld = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: chain.map((item, i) => {
          const entry = {
            "@type": "ListItem",
            position: i + 1,
            name: item.title,
          };
          if (item.route) {
            const resolved = this.$router.resolve({
              ...item.route,
              params: { ...item.route.params, lang },
            });
            entry.item = base + resolved.href;
          } else if (i === chain.length - 1) {
            entry.item = this.canonicalUrl;
          }
          return entry;
        }),
      };
      this.setBreadcrumbsJsonLd(ld);
    },
    buildPageMeta() {
      if (this.pageType === "brand" || this.pageType === "collection" || this.pageType === "outlet") {
        if (!this.hero) return null;
        return {
          metaTitle: this.hero.metaTitle || this.hero.title,
          metaDescription: this.hero.metaDescription || this.hero.description,
          ogImage: this.hero.bannerImage || this.hero.image || undefined,
        };
      }
      return null;
    },
  },
  beforeDestroy() {
    const html = document.getElementById("html");
    if (html) html.classList.remove("hide");
    // Сторінка без FAQ не має успадкувати чужу розмітку.
    this.removeFaqJsonLd();
  },
};
